import { generalData } from "Shared/Constants/sidebar";
import { MODULES, PERMISSIONS } from "Shared/Types/permissions";
import * as routes from "Shared/Constants/routes";
import { CommandKind, CommandPage, CreateKind } from "./types";

export interface CommandPermissions {
  has_full_access: boolean;
  modules: Set<MODULES>;
  available_permissions: Set<PERMISSIONS>;
}

type Rule = {
  module: MODULES;
  full: PERMISSIONS;
  view: PERMISSIONS;
  create?: PERMISSIONS;
};
const rules: Partial<Record<CommandKind, Rule>> = {
  vehicle: {
    module: MODULES.VEHICLES,
    full: PERMISSIONS.FULL_VEHICLE_ACCESS,
    view: PERMISSIONS.VIEW_VEHICLE,
    create: PERMISSIONS.CREATE_VEHICLE,
  },
  workOrder: {
    module: MODULES.WORK_ORDERS,
    full: PERMISSIONS.FULL_WORK_ORDERS_ACCESS,
    view: PERMISSIONS.VIEW_WORK_ORDERS,
    create: PERMISSIONS.CREATE_WORK_ORDERS,
  },
  issue: {
    module: MODULES.ISSUES,
    full: PERMISSIONS.FULL_ISSUES_ACCESS,
    view: PERMISSIONS.VIEW_ISSUES,
  },
  serviceReminder: {
    module: MODULES.VEHICLE_SERVICE_REMINDERS,
    full: PERMISSIONS.FULL_VEHICLE_SERVICE_REMINDERS_ACCESS,
    view: PERMISSIONS.VIEW_VEHICLE_SERVICE_REMINDERS,
    create: PERMISSIONS.CREATE_VEHICLE_SERVICE_REMINDERS,
  },
  vehicleReminder: {
    module: MODULES.VEHICLE_RENEWAL_REMINDERS,
    full: PERMISSIONS.FULL_VEHICLE_RENEWAL_REMINDERS_ACCESS,
    view: PERMISSIONS.VIEW_VEHICLE_RENEWAL_REMINDERS,
    create: PERMISSIONS.CREATE_VEHICLE_RENEWAL_REMINDERS,
  },
  contactReminder: {
    module: MODULES.CONTACT_RENEWAL_REMINDERS,
    full: PERMISSIONS.FULL_CONTACT_RENEWAL_REMINDERS_ACCESS,
    view: PERMISSIONS.VIEW_CONTACT_RENEWAL_REMINDERS,
    create: PERMISSIONS.CREATE_CONTACT_RENEWAL_REMINDERS,
  },
  vendor: {
    module: MODULES.VENDORS,
    full: PERMISSIONS.FULL_VENDORS_ACCESS,
    view: PERMISSIONS.VIEW_VENDORS,
    create: PERMISSIONS.CREATE_VENDORS,
  },
};
const kindRoutes: Record<string, CommandKind> = {
  [routes.UNITS]: "vehicle",
  [routes.WORK_ORDERS]: "workOrder",
  [routes.ISSUES]: "issue",
  [routes.REMINDERS_SERVICES]: "serviceReminder",
  [routes.REMINDERS_VEHICLES]: "vehicleReminder",
  [routes.REMINDERS_CONTACTS]: "contactReminder",
  [routes.VENDORS]: "vendor",
};

export function getCommandAccess(permissions?: CommandPermissions | null) {
  const allowedKinds: CommandKind[] = [];
  const allowedCreateKinds: CreateKind[] = [];
  const pages: CommandPage[] = [];
  if (!permissions) return { allowedKinds, allowedCreateKinds, pages };
  const full = permissions.has_full_access;
  for (const [kind, rule] of Object.entries(rules) as [CommandKind, Rule][]) {
    const enabled = full || permissions.modules.has(rule.module);
    if (!enabled) continue;
    if (
      full ||
      permissions.available_permissions.has(rule.full) ||
      permissions.available_permissions.has(rule.view)
    )
      allowedKinds.push(kind);
    if (
      rule.create &&
      (full ||
        permissions.available_permissions.has(rule.full) ||
        permissions.available_permissions.has(rule.create))
    )
      allowedCreateKinds.push(kind as CreateKind);
  }
  // The data layer independently checks each legacy global-search category's grants.
  allowedKinds.push("other");
  type NavEntry = {
    title: string;
    url?: string;
    modules?: MODULES[];
    permissions?: PERMISSIONS[];
    subMenu?: NavEntry[];
  };
  const add = (entry: NavEntry, group: string) => {
    if (
      !full &&
      entry.modules?.length &&
      !entry.modules.some((module) => permissions.modules.has(module))
    )
      return;
    if (
      !full &&
      entry.permissions?.length &&
      !entry.permissions.some((permission) =>
        permissions.available_permissions.has(permission),
      )
    )
      return;
    if (entry.subMenu?.length) {
      entry.subMenu.forEach((child) => add(child, `${group} ${entry.title}`));
      return;
    }
    if (!entry.url || entry.url.includes("*")) return;
    const kind = kindRoutes[entry.url];
    if (kind && !allowedKinds.includes(kind)) return;
    if (pages.some((page) => page.href === entry.url)) return;
    pages.push({
      key: `page:${entry.url}`,
      title: entry.title === "Units" ? "Vehicles / Units" : entry.title,
      href: entry.url,
      keywords: group,
      kind,
    });
  };
  generalData.forEach((group) =>
    group.subMenu.forEach((entry) => add(entry as NavEntry, group.title)),
  );
  return { allowedKinds, allowedCreateKinds, pages };
}
